import React, { useEffect, useId, useMemo, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ShieldCheckIcon, LanguageIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import AnimatedModal from '../ui/AnimatedModal';
import ConsentNoticeDocument from './ConsentNoticeDocument';

const LANGUAGE_PREF_KEY = 'tc_consent_notice_lang';

function readLanguagePref() {
  try {
    return window.localStorage.getItem(LANGUAGE_PREF_KEY) || '';
  } catch {
    return '';
  }
}

function saveLanguagePref(code) {
  try {
    window.localStorage.setItem(LANGUAGE_PREF_KEY, code);
  } catch {
    /* private mode / storage blocked — the choice just isn't remembered */
  }
}

/**
 * The hiring organisation's own DPDP consent notice (a per-client feature toggle, e.g.
 * `ajanta_consent_notice`). The apply page opens it from its checkbox; it never opens by
 * itself. Every word, button labels included, comes from the published notice in the
 * language the candidate picks.
 *
 * mode 'consent': 18+ checkbox + Agree / Close. Close records nothing; the apply form stays
 *   blocked until the candidate agrees.
 * mode 'review': read-only re-display after agreeing; one Close button.
 *
 * Sized for phones, tablets and laptops: the card never exceeds the visible viewport (dvh, so
 * mobile browser bars don't hide the buttons), only the notice body scrolls, and on very short
 * screens (phone in landscape) the whole card scrolls so the buttons are always reachable.
 */
const ConsentNoticeModal = ({
  isOpen,
  notice,
  mode = 'consent',
  submitting = false,
  errorMessage = '',
  onAgree,
  onClose,
}) => {
  const languages = useMemo(() => notice?.languages || [], [notice]);
  const [langCode, setLangCode] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [showAgeHint, setShowAgeHint] = useState(false);
  const titleId = useId();
  const ageId = useId();

  // Pick the language once per notice: the candidate's last choice, else the org default.
  useEffect(() => {
    if (!languages.length) return;
    const pref = readLanguagePref();
    const initial = languages.some((l) => l.code === pref) ? pref : notice.defaultLanguage;
    setLangCode(languages.some((l) => l.code === initial) ? initial : languages[0].code);
  }, [languages, notice?.defaultLanguage]);

  useEffect(() => {
    if (isOpen) setShowAgeHint(false);
  }, [isOpen]);

  const lang = languages.find((l) => l.code === langCode) || languages[0];
  if (!notice || !lang) return null;
  const { document: doc } = lang;
  const { labels } = doc;
  const isReview = mode === 'review';

  const handleLanguageChange = (code) => {
    setLangCode(code);
    saveLanguagePref(code);
  };

  const handleAgree = () => {
    if (!ageConfirmed) {
      setShowAgeHint(true);
      return;
    }
    onAgree?.(lang.code);
  };

  const handleClose = () => {
    if (!submitting) onClose?.();
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBackdrop={false}
      zIndex={60}
      contentClassName="w-full sm:w-[calc(100%-2rem)] max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      backdropClassName="bg-gray-900/60"
      ariaLabelledBy={titleId}
    >
      <div
        className="flex max-h-[calc(100dvh-2rem)] flex-col sm:max-h-[calc(100dvh-4rem)] [@media(max-height:500px)]:overflow-y-auto"
        dir={lang.dir}
        lang={lang.code}
        data-testid="consent-notice-modal"
      >
        {/* ── Header: title + language picker ── */}
        <div className="relative z-10 flex flex-col gap-3 border-b border-gray-100 bg-gradient-to-br from-slate-50 to-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 sm:py-5">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-electric-blue/10 text-electric-blue sm:h-10 sm:w-10">
              <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
            </span>
            <h2 id={titleId} className="text-base font-bold leading-snug text-gray-900 sm:text-lg">
              {doc.title}
            </h2>
          </div>
          {languages.length > 1 && (
            // Same Listbox look as the City / custom-field pickers on the apply page.
            <Listbox value={lang.code} onChange={handleLanguageChange}>
              {({ open }) => (
                <div className="relative w-full flex-shrink-0 sm:w-56">
                  <Listbox.Label className="sr-only">{labels.languageLabel}</Listbox.Label>
                  <Listbox.Button
                    data-testid="consent-notice-language"
                    className="relative w-full cursor-pointer rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-left text-sm text-gray-900 shadow-sm transition-all focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/20"
                  >
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <LanguageIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="block truncate font-medium">{lang.label}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronDownIcon
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={React.Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute right-0 z-[100] mt-1 max-h-[min(18rem,55dvh)] w-full overflow-auto overscroll-contain rounded-xl border border-gray-100 bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                      {languages.map((l) => (
                        <Listbox.Option
                          key={l.code}
                          value={l.code}
                          data-testid={`consent-notice-language-${l.code}`}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2.5 pl-4 pr-9 transition-colors ${
                              active ? 'bg-electric-blue-50 text-electric-blue' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className="flex items-baseline justify-between gap-3" dir="ltr">
                                <span className={`truncate ${selected ? 'font-semibold text-electric-blue' : 'font-normal'}`} dir={l.dir}>
                                  {l.label}
                                </span>
                                {l.englishName && l.englishName !== l.label && (
                                  <span className="flex-shrink-0 text-xs text-gray-400">{l.englishName}</span>
                                )}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-electric-blue">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>
          )}
        </div>

        {/* ── Notice body: the only part that scrolls (except on very short screens) ── */}
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-7 [@media(max-height:500px)]:flex-none [@media(max-height:500px)]:overflow-visible"
          tabIndex={0}
        >
          <ConsentNoticeDocument document={doc} dir={lang.dir} lang={lang.code} />
        </div>

        {/* ── Footer: 18+ confirmation + buttons ── */}
        <div className="border-t border-gray-100 bg-gray-50/80 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-7">
          {!isReview && (
            <>
              <label
                htmlFor={ageId}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border bg-white p-3 transition-colors ${
                  showAgeHint && !ageConfirmed ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200'
                }`}
              >
                <input
                  id={ageId}
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => {
                    setAgeConfirmed(e.target.checked);
                    setShowAgeHint(false);
                  }}
                  data-testid="consent-notice-age"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-electric-blue focus:ring-electric-blue sm:h-4 sm:w-4"
                />
                <span className="text-[13px] leading-snug text-gray-800">{labels.ageConfirmation}</span>
              </label>
              {showAgeHint && !ageConfirmed && (
                <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{labels.ageRequired}</p>
              )}
              {errorMessage && (
                <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-200" role="alert">
                  {errorMessage}
                </p>
              )}
              <p className="mt-3 text-xs text-gray-500">{labels.closingInstruction}</p>
            </>
          )}
          <div className="mt-3 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              data-testid="consent-notice-close"
              className="w-full rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 sm:w-auto sm:py-2.5"
            >
              {labels.close}
            </button>
            {!isReview && (
              <button
                type="button"
                onClick={handleAgree}
                disabled={submitting}
                aria-disabled={!ageConfirmed}
                data-testid="consent-notice-agree"
                className={`w-full rounded-lg px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 sm:w-auto sm:py-2.5 ${
                  ageConfirmed ? 'bg-electric-blue hover:bg-electric-blue-600' : 'bg-electric-blue/50'
                } disabled:cursor-wait disabled:opacity-60`}
              >
                {submitting ? '…' : labels.agree}
              </button>
            )}
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default ConsentNoticeModal;
